import { updateNewsCardExcerpts } from "./news-card-excerpt";
import { observeNewAnimations } from "./scroll-animations";

/**
 * Initialize infinite scroll functionality for posts
 * Loads more posts via AJAX when user scrolls near the bottom
 */
export function initInfiniteScroll(): void {
  const container = document.getElementById("posts-container");
  const loader = document.getElementById("infinite-scroll-loader");
  const scrollData = document.getElementById("infinite-scroll-data");

  // Exit if elements don't exist
  if (!container || !scrollData) return;

  // Get data attributes
  const maxPages = parseInt(scrollData.dataset.maxPages || "1");
  let currentPage = parseInt(scrollData.dataset.currentPage || "1");
  const excludedPost = scrollData.dataset.excludedPost || "0";
  let isLoading = false;

  // Exit if only one page
  if (maxPages <= 1) return;

  // Create intersection observer
  const observerOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: "400px",
    threshold: 0,
  };

  const loadMorePosts = async (): Promise<void> => {
    if (isLoading || currentPage >= maxPages) return;

    isLoading = true;
    currentPage++;

    // Show loader
    if (loader) {
      loader.classList.remove("hidden");
    }

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("action", "load_more_posts");
      formData.append("page", currentPage.toString());
      formData.append("excluded_post", excludedPost);
      formData.append("nonce", window.rediGroupAjax?.nonce || "");

      // Fetch more posts
      const response = await fetch(
        window.rediGroupAjax?.ajaxUrl || "/wp-admin/admin-ajax.php",
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: AjaxResponse<LoadMorePostsResponse> = await response.json();

      if (data.success && data.data.html) {
        // Create temporary container
        const temp = document.createElement("div");
        temp.innerHTML = data.data.html;

        // Append new posts to container
        while (temp.firstChild) {
          container.appendChild(temp.firstChild);
        }

        // Update excerpts for new cards
        updateNewsCardExcerpts();

        // Observe new cards for animations
        observeNewAnimations(container);

        // Check if there are more posts
        if (!data.data.has_more) {
          // No more posts - just hide loader and stop observing
          if (loader) loader.classList.add("hidden");
          if (sentinel) observer.unobserve(sentinel);
        }
      }
    } catch (error) {
      console.error("Error loading more posts:", error);
      // Stop observing on error to prevent repeated failed attempts
      if (sentinel) observer.unobserve(sentinel);
    } finally {
      isLoading = false;
      if (loader) loader.classList.add("hidden");
    }
  };

  // Create sentinel element for intersection observer
  const sentinel = document.createElement("div");
  sentinel.className = "infinite-scroll-sentinel";
  container.parentElement?.appendChild(sentinel);

  // Create observer
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        loadMorePosts();
      }
    });
  }, observerOptions);

  // Start observing
  observer.observe(sentinel);
}

